import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  CalendarPlus,
  Plus,
  ChevronDown,
  ChevronRight,
  Wallet,
  RotateCcw,
} from 'lucide-react';
import {
  useAddDeuda,
  useClientsList,
  useClientSnapshot,
  useDias,
  usePagar,
  useRevertir,
  useSyncClient,
} from '@/hooks/useClients';
import { useServers } from '@/hooks/useServers';
import { StatusPill } from '@/components/StatusPill';
import { Modal } from '@/components/Modal';
import { formatCurrency } from '@/lib/utils';
import type { ClientCacheRow, ClientStatus, DeudaItem } from '@/shared/types';

interface DialogState {
  kind: 'dias' | 'add_deuda' | null;
  row?: ClientCacheRow;
}

export function ClientsPage() {
  const [status, setStatus] = useState<ClientStatus | ''>('');
  const [serverId, setServerId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const pageSize = 50;

  const servers = useServers();
  const list = useClientsList({
    status: status || undefined,
    server_id: serverId || undefined,
    search: search || undefined,
    page,
    page_size: pageSize,
  });

  const [dialog, setDialog] = useState<DialogState>({ kind: null });

  const totalPages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.page_size)) : 1;

  function toggleRow(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-moss-200">Colegios</h1>
        <p className="mt-1 text-sm text-moss-300/70">
          {list.data?.total ?? 0} resultados {list.isFetching && '· actualizando…'}
          <span className="ml-2 text-moss-300/50">— Click en una fila para ver y pagar deudas</span>
        </p>
      </div>

      <div className="glass-card flex flex-wrap items-center gap-3 p-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-moss-400" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar por nombre, db o dominio…"
            className="input-base pl-10"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ClientStatus | '');
            setPage(1);
          }}
          className="input-base h-11 w-auto"
        >
          <option value="">Todos los estados</option>
          <option value="al_dia">Al día</option>
          <option value="en_gracia">En gracia</option>
          <option value="caducado">Caducado</option>
        </select>
        <select
          value={serverId}
          onChange={(e) => {
            setServerId(e.target.value);
            setPage(1);
          }}
          className="input-base h-11 w-auto"
        >
          <option value="">Todos los servidores</option>
          {servers.data?.rows.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-ink-700/70 bg-ink-800/40 text-left text-xs uppercase tracking-wide text-moss-300/70">
              <tr>
                <th className="w-8 px-2 py-3"></th>
                <th className="px-4 py-3">Colegio</th>
                <th className="px-4 py-3">Servidor</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Adeudado</th>
                <th className="px-4 py-3 text-right">Vencido</th>
                <th className="px-4 py-3 text-right">Gracia rest.</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.isLoading && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-moss-300/60">
                    Cargando…
                  </td>
                </tr>
              )}
              {list.data?.rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-moss-300/60">
                    Sin colegios todavía. Agrega un servidor en{' '}
                    <Link to="/servidores" className="text-mustard-300 underline">
                      Servidores
                    </Link>{' '}
                    y espera al primer sync.
                  </td>
                </tr>
              )}
              {list.data?.rows.map((r) => {
                const isOpen = expanded.has(r.id);
                return (
                  <RowGroup
                    key={r.id}
                    row={r}
                    isOpen={isOpen}
                    onToggle={() => toggleRow(r.id)}
                    onDias={() => setDialog({ kind: 'dias', row: r })}
                    onAddDeuda={() => setDialog({ kind: 'add_deuda', row: r })}
                  />
                );
              })}
            </tbody>
          </table>
        </div>

        {list.data && list.data.total > pageSize && (
          <div className="flex items-center justify-between border-t border-ink-700/70 bg-ink-800/40 px-4 py-3 text-xs text-moss-300/70">
            <div>
              Página {list.data.page} de {totalPages} · {list.data.total} resultados
            </div>
            <div className="flex gap-2">
              <button
                className="btn-ghost h-8 px-3 text-xs"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={list.data.page <= 1}
              >
                Anterior
              </button>
              <button
                className="btn-ghost h-8 px-3 text-xs"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={list.data.page >= totalPages}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      <ActionDialog state={dialog} onClose={() => setDialog({ kind: null })} />
    </div>
  );
}

function RowGroup({
  row,
  isOpen,
  onToggle,
  onDias,
  onAddDeuda,
}: {
  row: ClientCacheRow;
  isOpen: boolean;
  onToggle: () => void;
  onDias: () => void;
  onAddDeuda: () => void;
}) {
  const sync = useSyncClient();

  return (
    <>
      <tr
        className="cursor-pointer border-b border-ink-700/40 hover:bg-ink-700/30"
        onClick={onToggle}
      >
        <td className="px-2 py-3 text-moss-300">
          {isOpen ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </td>
        <td className="px-4 py-3">
          <div className="font-medium text-moss-200">{row.system_title}</div>
          <div className="text-[11px] text-moss-300/60">
            {row.dominio} · {row.db_name} · y{row.year}
          </div>
        </td>
        <td className="px-4 py-3 text-moss-300">{row.server_name}</td>
        <td className="px-4 py-3">
          <StatusPill value={row.estado} />
        </td>
        <td className="px-4 py-3 text-right font-mono text-moss-200">
          {formatCurrency(row.total_adeudado)}
        </td>
        <td className="px-4 py-3 text-right text-moss-300">{row.dias_vencidos}d</td>
        <td className="px-4 py-3 text-right text-moss-300">{row.dias_gracia_restantes}d</td>
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              title="Configurar días de gracia"
              onClick={onDias}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-ink-600 text-moss-300 transition hover:border-mustard-500/60 hover:text-mustard-300"
            >
              <CalendarPlus className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Agregar nueva deuda"
              onClick={onAddDeuda}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-ink-600 text-moss-300 transition hover:border-mustard-500/60 hover:text-mustard-300"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Resincronizar"
              onClick={() => sync.mutate({ serverId: row.server_id, dbName: row.db_name })}
              disabled={sync.isPending}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-ink-600 text-moss-300 transition hover:border-mustard-500/60 hover:text-mustard-300 disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${sync.isPending ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </td>
      </tr>
      {isOpen && (
        <tr className="border-b border-ink-700/40 bg-ink-900/40">
          <td colSpan={8} className="px-4 py-4">
            <ClientDeudasInline row={row} />
          </td>
        </tr>
      )}
    </>
  );
}

function ClientDeudasInline({ row }: { row: ClientCacheRow }) {
  const snap = useClientSnapshot(row.server_id, row.db_name);
  const pagar = usePagar();
  const revertir = useRevertir();

  if (snap.isLoading) {
    return <div className="px-2 py-3 text-xs text-moss-300/60">Cargando deudas…</div>;
  }
  if (snap.isError) {
    return (
      <div className="px-2 py-3 text-xs text-danger">
        No se pudieron cargar las deudas: {(snap.error as Error).message}
      </div>
    );
  }
  if (!snap.data) return null;

  // Sort chronologically (year + mes_id), so the user pays in order.
  const deudas: DeudaItem[] = [...snap.data.deudas].sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;
    return a.mes_id - b.mes_id;
  });

  const today = new Date();
  function daysOverdue(fecha: string): number {
    const d = new Date(fecha);
    if (isNaN(d.getTime())) return 0;
    const diff = Math.floor((today.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  }

  const MESES = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ];
  function formatMonth(fecha: string, year: number): string {
    const m = /^\d{4}-(\d{2})-/.exec(fecha);
    const idx = m ? Number(m[1]) - 1 : 0;
    return `${MESES[idx] ?? '?'} ${year}`;
  }

  const alargar = snap.data.alargar_dias;
  const isPending = pagar.isPending || revertir.isPending;

  async function onPagar(d: DeudaItem) {
    if (!confirm(`¿Marcar como PAGADO el mes ${d.mes_id} (${d.fecha}, ${formatCurrency(Number(d.cantidad))})?`)) return;
    try {
      await pagar.mutateAsync({
        serverId: row.server_id,
        dbName: row.db_name,
        body: { mes_id: d.mes_id },
      });
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
  }

  async function onRevertir(d: DeudaItem) {
    if (!confirm(`¿REVERTIR el pago del mes ${d.mes_id} (${d.fecha})?`)) return;
    try {
      await revertir.mutateAsync({
        serverId: row.server_id,
        dbName: row.db_name,
        body: { mes_id: d.mes_id },
      });
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 px-2 text-xs text-moss-300/80">
        <span>
          <span className="text-moss-300/60">Días de gracia configurados:</span>{' '}
          <span className="font-mono font-semibold text-mustard-300">{alargar}d</span>
          <span className="ml-1 text-moss-300/50">(se aplica a todas las deudas)</span>
        </span>
        <span className="text-moss-300/40">·</span>
        <span>
          <span className="text-moss-300/60">Total adeudado:</span>{' '}
          <span className="font-mono font-semibold text-moss-200">
            {formatCurrency(snap.data.summary.total_adeudado)}
          </span>
        </span>
        <span className="text-moss-300/40">·</span>
        <span>
          <span className="text-moss-300/60">Estado:</span>{' '}
          <StatusPill value={snap.data.summary.estado} />
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-ink-700/70">
        <table className="w-full text-xs">
          <thead className="bg-ink-800/60 text-left uppercase tracking-wide text-moss-300/70">
            <tr>
              <th className="w-12 px-3 py-2">Mes</th>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2 text-right">Monto</th>
              <th className="px-3 py-2 text-right">Días vencido</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2 text-right">Acción</th>
            </tr>
          </thead>
          <tbody>
            {deudas.map((d) => {
              const pagado = Number(d.pagado) > 0;
              const dv = pagado ? 0 : daysOverdue(d.fecha);
              const dentroDeGracia = !pagado && dv > 0 && dv <= alargar;
              const fueraDeGracia = !pagado && dv > alargar;
              return (
                <tr
                  key={d.deuda_id}
                  className="border-t border-ink-700/40 hover:bg-ink-700/30"
                >
                  <td className="px-3 py-2 font-mono text-moss-300">{d.mes_id}</td>
                  <td className="px-3 py-2 text-moss-200">
                    {formatMonth(d.fecha, d.year)}
                    <span className="ml-2 text-[10px] text-moss-300/50">{d.fecha}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-moss-200">
                    {formatCurrency(Number(d.cantidad))}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-moss-300">
                    {pagado ? '—' : `${dv}d`}
                  </td>
                  <td className="px-3 py-2">
                    {pagado ? (
                      <span className="pill-ok">Pagado</span>
                    ) : fueraDeGracia ? (
                      <span className="pill-danger">Caducado</span>
                    ) : dentroDeGracia ? (
                      <span className="pill-warn">En gracia</span>
                    ) : (
                      <span className="pill-warn">Pendiente</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1.5">
                      {pagado ? (
                        <button
                          type="button"
                          onClick={() => onRevertir(d)}
                          disabled={isPending}
                          className="inline-flex h-7 items-center gap-1 rounded-md border border-ink-600 px-2 text-[11px] font-medium text-moss-300 transition hover:border-danger/60 hover:text-danger disabled:opacity-50"
                        >
                          <RotateCcw className="h-3 w-3" />
                          Revertir
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => onPagar(d)}
                          disabled={isPending}
                          className="inline-flex h-7 items-center gap-1 rounded-md bg-mustard-500 px-2.5 text-[11px] font-semibold text-ink-900 transition hover:bg-mustard-400 disabled:opacity-50"
                        >
                          <Wallet className="h-3 w-3" />
                          Pagar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {deudas.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-moss-300/60">
                  Sin deudas registradas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ActionDialog({ state, onClose }: { state: DialogState; onClose: () => void }) {
  const dias = useDias();
  const addDeuda = useAddDeuda();

  const [diasVal, setDiasVal] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [fecha, setFecha] = useState('');

  const title = useMemo(() => {
    if (state.kind === 'dias') return 'Configurar días de gracia';
    if (state.kind === 'add_deuda') return 'Agregar nueva deuda';
    return '';
  }, [state.kind]);

  const isPending = dias.isPending || addDeuda.isPending;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!state.row) return;
    const ctx = { serverId: state.row.server_id, dbName: state.row.db_name };
    try {
      if (state.kind === 'dias') {
        await dias.mutateAsync({ ...ctx, body: { dias: Number(diasVal) } });
      } else if (state.kind === 'add_deuda') {
        await addDeuda.mutateAsync({
          ...ctx,
          body: { fecha, cantidad: Number(cantidad) },
        });
      }
      setDiasVal('');
      setCantidad('');
      setFecha('');
      onClose();
    } catch (err) {
      alert(`Error: ${(err as Error).message}`);
    }
  }

  return (
    <Modal open={state.kind !== null} onClose={onClose} title={title}>
      {state.row && (
        <p className="mb-4 text-xs text-moss-300/70">
          {state.row.system_title} <span className="text-moss-400">·</span> {state.row.db_name}
        </p>
      )}
      <form className="space-y-4" onSubmit={submit}>
        {state.kind === 'dias' && (
          <Field label="Días de gracia (0 a 365)">
            <input
              required
              type="number"
              min={0}
              max={365}
              value={diasVal}
              onChange={(e) => setDiasVal(e.target.value)}
              className="input-base"
              placeholder="10"
            />
          </Field>
        )}
        {state.kind === 'add_deuda' && (
          <>
            <Field label="Fecha (YYYY-MM-DD)">
              <input
                required
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="input-base"
              />
            </Field>
            <Field label="Cantidad">
              <input
                required
                type="number"
                step="0.01"
                value={cantidad}
                onChange={(e) => setCantidad(e.target.value)}
                className="input-base"
              />
            </Field>
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={isPending}>
            {isPending ? 'Enviando…' : 'Confirmar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wide text-moss-300">{label}</label>
      {children}
    </div>
  );
}
